/**
 * Shared point/path math for decorative place card frame styles (currently just "royal") so the
 * Konva canvas preview and the PDF export render the exact same wavy border and spiral corner
 * flourishes.
 */

export type FramePoint = [number, number];

/**
 * Samples a smooth scalloped/wavy outline around a `width` x `height` rectangle, starting at the
 * origin. Each edge's wavelength is snapped to a whole number of periods so the wave crosses zero
 * exactly at every corner, keeping the outline seamless and closed.
 */
export function buildWavyRectPoints(
  width: number,
  height: number,
  amplitude: number,
  targetWavelength: number
): FramePoint[] {
  const points: FramePoint[] = [];
  const samplesPerPeriod = 8;

  const edgeWavelength = (length: number): number => {
    const periods = Math.max(2, Math.round(length / targetWavelength));
    return length / periods;
  };

  const sampleEdge = (
    length: number,
    wavelength: number,
    toPoint: (distance: number, wave: number) => FramePoint
  ) => {
    const periods = Math.max(1, Math.round(length / wavelength));
    const steps = Math.max(8, periods * samplesPerPeriod);
    for (let i = 0; i <= steps; i++) {
      const distance = (i / steps) * length;
      const wave = amplitude * Math.sin((distance / wavelength) * Math.PI * 2);
      points.push(toPoint(distance, wave));
    }
  };

  const topWavelength = edgeWavelength(width);
  const sideWavelength = edgeWavelength(height);

  sampleEdge(width, topWavelength, (d, wave) => [d, wave]);
  sampleEdge(height, sideWavelength, (d, wave) => [width - wave, d]);
  sampleEdge(width, topWavelength, (d, wave) => [width - d, height - wave]);
  sampleEdge(height, sideWavelength, (d, wave) => [wave, height - d]);

  return points;
}

/** Samples an outward Archimedean spiral starting at the origin — used for corner flourishes. */
export function buildSpiralPoints(turns: number, maxRadius: number, steps: number): FramePoint[] {
  const points: FramePoint[] = [];
  const totalAngle = turns * Math.PI * 2;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const angle = t * totalAngle;
    const radius = t * maxRadius;
    points.push([radius * Math.cos(angle), radius * Math.sin(angle)]);
  }
  return points;
}

/**
 * Converts sampled points into a smooth SVG path using quadratic curves through edge midpoints —
 * a standard, cheap way to get a smooth line through arbitrary points without full spline math.
 */
export function smoothSvgPathFromPoints(points: FramePoint[], closed: boolean): string {
  if (points.length < 2) {
    return '';
  }
  const pts = closed ? [...points, points[0], points[1]] : points;
  let d = `M ${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`;
  for (let i = 0; i < pts.length - 2; i++) {
    const [x1, y1] = pts[i + 1];
    const [x2, y2] = pts[i + 2];
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    d += ` Q ${x1.toFixed(2)},${y1.toFixed(2)} ${mx.toFixed(2)},${my.toFixed(2)}`;
  }
  if (closed) {
    d += ' Z';
  }
  return d;
}
